import { useCallback, useRef, useState } from 'preact/hooks'
import type { JSX } from 'preact'
import type { MountOptions } from '../mount'
import type { DetectedInvoice } from '../types'
import { analyzeInvoice, RateLimitError } from '../lib/api'
import AnalyzingState from './AnalyzingState'

const DEFAULT_MAX_MB = 50
const DEFAULT_MAX_PAGES = 100

type Status = 'idle' | 'analyzing' | 'error'

type Props = {
  options: MountOptions
  onAnalyzed: (file: File, invoices: DetectedInvoice[]) => void
  /** Called when the backend returns a 429. App swaps to RateLimitView. */
  onRateLimited: () => void
}

export default function Dropzone({ options, onAnalyzed, onRateLimited }: Props) {
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const maxMb = options.maxFileSizeMb ?? DEFAULT_MAX_MB
  const maxPages = options.maxPages ?? DEFAULT_MAX_PAGES
  const maxBytes = maxMb * 1024 * 1024

  const handleFile = useCallback(
    async (file: File) => {
      setError(null)

      if (file.type !== 'application/pdf') {
        setStatus('error')
        setError('Please upload a PDF file.')
        return
      }
      if (file.size > maxBytes) {
        setStatus('error')
        setError(`File is too large. Maximum is ${maxMb} MB.`)
        return
      }

      setStatus('analyzing')

      try {
        const result = await analyzeInvoice(file, options.apiBase)

        if (result.pageCount > maxPages) {
          setStatus('error')
          setError(
            `PDF has ${result.pageCount} pages. Free tier supports up to ${maxPages}.`,
          )
          return
        }

        onAnalyzed(file, result.invoices)
        setStatus('idle')
      } catch (e) {
        if (e instanceof RateLimitError) {
          // Bubble up — App.tsx swaps the dropzone for the friendly
          // rate-limit upsell view.
          onRateLimited()
          return
        }
        console.error('[SpendifiqueInvoiceSplitter] analyze failed', e)
        setStatus('error')
        setError('Something went wrong analyzing the PDF. Please try again.')
      }
    },
    [maxBytes, maxMb, maxPages, onAnalyzed, onRateLimited, options.apiBase],
  )

  const onDrop = useCallback(
    (e: JSX.TargetedDragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setDragOver(false)
      const f = e.dataTransfer?.files?.[0]
      if (f) void handleFile(f)
    },
    [handleFile],
  )

  const onSelect = useCallback(
    (e: JSX.TargetedEvent<HTMLInputElement, Event>) => {
      const f = e.currentTarget.files?.[0]
      if (f) void handleFile(f)
    },
    [handleFile],
  )

  const onDragOver = (e: JSX.TargetedDragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(true)
  }

  const onDragLeave = () => setDragOver(false)

  return (
    <div
      class={`sis-dropzone ${dragOver ? 'sis-dropzone--over' : ''}`}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        onChange={onSelect}
        class="sis-hidden-input"
        id="sis-pdf-input"
      />
      {status === 'analyzing' ? (
        <AnalyzingState />
      ) : (
        <>
          <p class="sis-headline">Drop your PDF here</p>
          <p class="sis-or">or</p>
          <label htmlFor="sis-pdf-input" class="sis-button">
            Choose file
          </label>
          <p class="sis-hint">
            PDF, up to {maxMb} MB, {maxPages} pages
          </p>
          {error && <p class="sis-error">{error}</p>}
        </>
      )}
    </div>
  )
}
